import React, { useState, useEffect, useMemo } from 'react';
import api from './api';

// Helpers
const formatRound = (num) => Math.round(num || 0).toLocaleString('en-IN');
const calculateDueDate = (date, terms) => {
    const d = new Date(date);
    d.setDate(d.getDate() + parseInt(terms || 0));
    return d;
};
const getDueStatus = (date, terms) => {
    if (!terms) return { date: '-', days: '-', status: 'Normal' };
    const dueDate = calculateDueDate(date, terms);
    const today = new Date();
    const diffTime = dueDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    let status = 'Normal';
    if (diffDays < 0) status = 'Overdue';
    else if (diffDays <= 3) status = 'Urgent';
    return { date: dueDate.toLocaleDateString('en-GB'), days: diffDays, status };
};

const AccountsReceivable = () => {
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Receipt Modal State
    const [showModal, setShowModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [recForm, setRecForm] = useState({ amount: '', mode: 'Cheque', ref: '' });

    // 1. Fetch Sales
    const fetchSales = async () => {
        setLoading(true);
        try {
            const res = await api.get('/sales');
            // Filter only unpaid/partial sales
            const pending = res.data.filter(s => s.paymentStatus !== 'Paid');
            setSales(pending);
        } catch (err) { alert("Error loading receivables"); }
        finally { setLoading(false); }
    };

    useEffect(() => { fetchSales(); }, []);

    // 2. Dashboard Math
    const stats = useMemo(() => {
        let total = 0, billDue = 0, cashDue = 0;
        const months = {};

        sales.forEach(s => {
            const pending = (s.finalAmount || 0) - (s.receivedAmount || 0);
            total += pending;
            if (s.billType === 'Cash') cashDue += pending;
            else billDue += pending;

            // Monthly Projection
            if (s.paymentTermsDays) {
                const due = calculateDueDate(s.saleDate, s.paymentTermsDays);
                const key = due.toLocaleString('default', { month: 'short', year: '2-digit' });
                if (!months[key]) months[key] = 0;
                months[key] += pending;
            }
        });

        return { total, billDue, cashDue, months };
    }, [sales]);

    // 3. Handle Receive Click
    const openReceiptModal = (sale) => {
        const pending = (sale.finalAmount || 0) - (sale.receivedAmount || 0);
        setSelectedSale(sale);
        setRecForm({ amount: pending, mode: 'Cheque', ref: '' });
        setShowModal(true);
    };

    // 4. Submit Receipt
    const submitReceipt = async (e) => {
        e.preventDefault();
        try {
            await api.post('/receipts', {
                saleId: selectedSale.saleId, // Using _id logic internally? check controller
                saleId: selectedSale._id,     // SEND MONGO ID
                buyer: selectedSale.buyer._id,
                amountReceived: recForm.amount,
                paymentMode: recForm.mode,
                referenceNumber: recForm.ref
            });
            alert("✅ Receipt Recorded!");
            setShowModal(false);
            fetchSales(); 
        } catch (err) {
            alert("Error: " + (err.response?.data?.error || err.message));
        }
    };

    if (loading) return <div className="p-5 text-center">Loading Receivables...</div>;

    return (
        <div className="container mt-4 mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold">Accounts Receivable (Incoming)</h4>
                <button className="btn btn-sm btn-outline-dark" onClick={fetchSales}>↻ Refresh</button>
            </div>

            {/* DASHBOARD CARDS */}
            <div className="row mb-4 g-3">
                <div className="col-12 col-md-3">
                    <div className="card bg-success text-white h-100 shadow-sm">
                        <div className="card-body text-center">
                            <small className="opacity-75">Total To Collect</small>
                            <h3 className="fw-bold mb-0">₹ {formatRound(stats.total)}</h3>
                        </div>
                    </div>
                </div>

                <div className="col-6 col-md-3">
                    <div className="card h-100 shadow-sm">
                        <div className="card-body p-2 p-md-3">
                            <div className="d-flex justify-content-between mb-2 border-bottom pb-1">
                                <span className="text-muted small">Bill</span>
                                <span className="fw-bold small">₹ {formatRound(stats.billDue)}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted small">Cash</span>
                                <span className="fw-bold text-success small">₹ {formatRound(stats.cashDue)}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <div className="card bg-light h-100 border-0">
                        <div className="card-body py-2">
                            <small className="fw-bold text-muted">Incoming by Month</small>
                            <div className="d-flex gap-2 overflow-auto mt-2" style={{whiteSpace:'nowrap'}}>
                                {Object.entries(stats.months).sort().map(([month, amount]) => (
                                    <div key={month} className="bg-white border px-3 py-1 rounded text-center">
                                        <div className="small fw-bold">{month}</div>
                                        <div className="text-success small">₹ {(amount/100000).toFixed(1)}L</div>
                                    </div>
                                ))}
                                {Object.keys(stats.months).length === 0 && <span className="text-muted small">No upcoming.</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* DESKTOP TABLE */}
            <div className="card shadow-sm border-0 d-none d-md-block">
                <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                        <thead className="table-dark">
                            <tr>
                                <th>Inv No</th>
                                <th>Buyer</th>
                                <th>Type</th>
                                <th>Date</th>
                                <th>Due Date</th>
                                <th>Days Left</th>
                                <th className="text-end">Inv Amt</th>
                                <th className="text-end">Pending</th>
                                <th className="text-center">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.map(s => {
                                const due = getDueStatus(s.saleDate, s.paymentTermsDays);
                                const pending = (s.finalAmount || 0) - (s.receivedAmount || 0);
                                let rowClass = "";
                                if (due.status === 'Overdue') rowClass = "table-danger";
                                else if (due.status === 'Urgent') rowClass = "table-warning";

                                return (
                                    <tr key={s._id} className={rowClass}>
                                        <td className="fw-bold text-primary">{s.saleId}</td>
                                        <td>{s.buyer?.companyName}</td>
                                        <td><span className={`badge ${s.billType === 'Cash' ? 'bg-success' : 'bg-secondary'}`}>{s.billType}</span></td>
                                        <td>{new Date(s.saleDate).toLocaleDateString('en-GB')}</td>
                                        <td>{due.date}</td>
                                        <td>
                                            {due.days !== '-' && (
                                                <span className={`fw-bold ${due.days < 0 ? 'text-danger' : due.days <= 3 ? 'text-warning' : 'text-success'}`}>
                                                    {due.days < 0 ? `${Math.abs(due.days)} Days Late` : `${due.days} Days Left`}
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-end">{formatRound(s.finalAmount)}</td>
                                        <td className="text-end fw-bold">₹ {formatRound(pending)}</td>
                                        <td className="text-center">
                                            <button className="btn btn-sm btn-success" onClick={() => openReceiptModal(s)}>Receive</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* MOBILE CARDS */}
            <div className="d-block d-md-none">
                {sales.map(s => {
                    const due = getDueStatus(s.saleDate, s.paymentTermsDays);
                    const pending = (s.finalAmount || 0) - (s.receivedAmount || 0);
                    let borderClass = "border-start border-4 ";
                    if (due.status === 'Overdue') borderClass += "border-danger";
                    else if (due.status === 'Urgent') borderClass += "border-warning";
                    else borderClass += "border-success";

                    return (
                        <div key={s._id} className={`card mb-3 shadow-sm ${borderClass}`}>
                            <div className="card-body p-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                    <h6 className="mb-0 fw-bold">{s.buyer?.companyName}</h6>
                                    <span className="badge bg-light text-dark border">{s.saleId}</span>
                                </div>
                                <div className="d-flex justify-content-between small text-muted mb-2">
                                    <span>{new Date(s.saleDate).toLocaleDateString('en-GB')}</span>
                                    <span className={`${due.days < 0 ? 'text-danger fw-bold' : ''}`}>
                                        Due: {due.date}
                                    </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        <div className="small text-muted">Pending To Receive</div>
                                        <h5 className="mb-0 fw-bold text-success">₹ {formatRound(pending)}</h5>
                                    </div>
                                    <button className="btn btn-success btn-sm px-3" onClick={() => openReceiptModal(s)}>Receive</button>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* RECEIPT MODAL */}
            {showModal && (
                <div className="modal d-block" style={{backgroundColor:'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-success text-white">
                                <h5 className="modal-title">Record Receipt</h5>
                                <button className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
                            </div>
                            <form onSubmit={submitReceipt}>
                                <div className="modal-body">
                                    <div className="alert alert-secondary py-2 mb-3">
                                        <strong>{selectedSale?.buyer?.companyName}</strong><br/>
                                        Inv: {selectedSale?.saleId} | Pending: <strong>₹ {formatRound((selectedSale?.finalAmount || 0) - (selectedSale?.receivedAmount || 0))}</strong>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Amount Received</label>
                                        <input type="number" className="form-control" value={recForm.amount} onChange={e => setRecForm({...recForm, amount: e.target.value})} required />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Mode</label>
                                        <select className="form-select" value={recForm.mode} onChange={e => setRecForm({...recForm, mode: e.target.value})}>
                                            <option>Cheque</option>
                                            <option>RTGS</option>
                                            <option>Cash</option>
                                            <option>Angadia</option>
                                        </select>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Reference / Note</label>
                                        <input type="text" className="form-control" value={recForm.ref} onChange={e => setRecForm({...recForm, ref: e.target.value})} />
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                    <button type="submit" className="btn btn-success">Confirm</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AccountsReceivable;